import { useMemo, useState, useCallback } from 'react';
import { getModelById } from '../config/aiModels';
import { inferModelFromPrompt } from '../config/aiPromptInference';

const CATEGORY_NOUNS = { video: 'video', image: 'image', 'sound-effects': 'a sound effect', voiceover: 'a voiceover' };
const ASPECT_LABELS = { '16:9': 'landscape', '9:16': 'portrait', '1:1': 'square' };

/**
 * Derives the effective model (via prompt inference) and a human-readable hint.
 *
 * @param {string} selectedModel - The model explicitly chosen by the user
 * @param {string} prompt        - Current prompt text
 * @param {string} aspectRatio   - Current aspect ratio setting
 * @returns {{ effectiveModel, modelHint, userChangedModel, setUserChangedModel, handleModelChange }}
 */
export const useAiEffectiveModel = (selectedModel, prompt, aspectRatio, onModelChange) => {
  const [userChangedModel, setUserChangedModel] = useState(false);

  const effectiveModel = useMemo(() => {
    if (userChangedModel) return selectedModel;
    const inferred = inferModelFromPrompt(prompt);
    return inferred || selectedModel;
  }, [prompt, selectedModel, userChangedModel]);

  const modelHint = useMemo(() => {
    const model = getModelById(effectiveModel);
    const noun = CATEGORY_NOUNS[model?.category];
    if (!noun) return null;
    const hasAspect = model?.settings?.aspectRatio;
    const label = ASPECT_LABELS[aspectRatio] || aspectRatio;
    return `This creates ${hasAspect ? `a ${label} ` : ''}${noun}.`;
  }, [effectiveModel, aspectRatio]);

  const handleModelChange = useCallback((id) => {
    onModelChange(id);
    setUserChangedModel(true);
  }, [onModelChange]);

  return { effectiveModel, modelHint, userChangedModel, setUserChangedModel, handleModelChange };
};
